/**
 * Assault elite brute flavor variants — sticky by id-rank among wave brutes.
 * HUD nameKey + body affiliation (fillRemap) + greatsword bladeColor share the same rank.
 */
import { hexToColor, raidCatalog } from "../../data/raid-catalog";
import { RaidEnemyDefId } from "./raid-enemy";
import type { RaidUnit } from "./raid-types";

export type BruteVariantId = "stoneplate" | "ironhorn" | "graphite";

export type BruteVariantLook = {
  id: BruteVariantId;
  nameKey: string;
  affiliationKey: string;
  bladeColor: number;
};

type BruteVariantRow = {
  id: BruteVariantId;
  nameKey: string;
  affiliationKey: string;
  bladeColor: string;
};

const FALLBACK: readonly BruteVariantRow[] = [
  {
    id: "stoneplate",
    nameKey: "raid.boss.brute.stoneplate",
    affiliationKey: "brute_stoneplate",
    bladeColor: "#e0c888",
  },
  {
    id: "ironhorn",
    nameKey: "raid.boss.brute.ironhorn",
    affiliationKey: "brute_ironhorn",
    bladeColor: "#d0dce8",
  },
  {
    id: "graphite",
    nameKey: "raid.boss.brute.graphite",
    affiliationKey: "brute_graphite",
    bladeColor: "#a8b0c0",
  },
] as const;

function variantRows(): readonly BruteVariantRow[] {
  const raw = (
    raidCatalog.view as { bruteVariants?: readonly BruteVariantRow[] }
  ).bruteVariants;
  return raw && raw.length > 0 ? raw : FALLBACK;
}

/** Flavor name key for HUD (rank among all wave brutes by ascending id). */
export function bruteVariantNameKey(rankIndex: number): string {
  return bruteVariantLook(rankIndex).nameKey;
}

export function bruteVariantLook(rankIndex: number): BruteVariantLook {
  const rows = variantRows();
  const n = rows.length;
  const i = ((rankIndex % n) + n) % n;
  const row = rows[i]!;
  return {
    id: row.id,
    nameKey: row.nameKey,
    affiliationKey: row.affiliationKey,
    bladeColor: hexToColor(row.bladeColor),
  };
}

/**
 * Sticky id-rank among every brute spawned this wave (living or dead).
 * Matches elite HUD naming so visuals stay aligned after a kill.
 */
export function bruteWaveRankById(
  units: readonly RaidUnit[],
): Map<number, number> {
  const rank = new Map<number, number>();
  units
    .filter((u) => u.defId === RaidEnemyDefId.Brute)
    .slice()
    .sort((a, b) => a.id - b.id)
    .forEach((u, i) => rank.set(u.id, i));
  return rank;
}

export function bruteVariantLookForUnit(
  units: readonly RaidUnit[],
  unitId: number,
): BruteVariantLook {
  const rank = bruteWaveRankById(units).get(unitId) ?? 0;
  return bruteVariantLook(rank);
}
